import FilterListIcon from "@mui/icons-material/FilterList";
import { FC, ReactNode } from "react";
import { MuiOptionMenu } from "..";
import { ReactComponent as ResetAllIcon } from "../../assets/images/reset-all.svg";
import { gridSizeSmall } from "../../helpers/constants";
import { useGridContainer } from "../../hooks/useGridContainer";
import { useMediaQueries } from "../../hooks/useMediaQueries";
import { useSiteCore } from "../../hooks/useSiteCore";
import {
  LabelWithIconText,
  Separator,
  SeparatorDotted,
} from "../../styles/common.style";
import { MuiButtonTertiary } from "../button/MuiButton";
import { MuiGrid } from "../grid/MuiGrid";
import { SearchSaveFiltersSubHeaderWrapper } from "./SearchSaveFiltersSubHeader.style";

export interface ActionConfig {
  id: string;
  label: ReactNode;
  icon?: ReactNode;
  onClick: () => void;
  testId?: string;
  ariaLabel?: string;
  disabled?: boolean;
  showOutsideMobile?: boolean;
}

interface SearchSaveFiltersSubHeaderProps {
  onSavedFilterClick?: () => void;
  onResetAllClick?: () => void;
  anchorRef?: React.RefObject<HTMLButtonElement>;
  isSavedFilterDisabled?: boolean;
  resetButtonTestId?: string;
  savedFilterTestId?: string;
  additionalActions?: ActionConfig[];
}

export const SearchSaveFiltersSubHeader: FC<
  SearchSaveFiltersSubHeaderProps
> = ({
  onSavedFilterClick,
  onResetAllClick,
  anchorRef,
  isSavedFilterDisabled,
  resetButtonTestId = "reset-all",
  savedFilterTestId = "saved-filter",
  additionalActions = [],
}) => {
  const { commonLabels } = useSiteCore();
  const { isMobile } = useMediaQueries();
  const { gridSizeLarge: gridSize } = useGridContainer();

  // Default Reset button
  const resetAction: ActionConfig | null = onResetAllClick
    ? {
        id: "reset-all",
        label: commonLabels.resetAllSearch,
        icon: <ResetAllIcon />,
        onClick: onResetAllClick,
        testId: resetButtonTestId,
        showOutsideMobile: true, // always outside
      }
    : null;

  // Optional Saved Filter button
  const savedFilterAction: ActionConfig | null = onSavedFilterClick
    ? {
        id: "saved-filter",
        label: commonLabels.savedFilter,
        icon: <FilterListIcon />,
        onClick: onSavedFilterClick,
        testId: savedFilterTestId,
        disabled: isSavedFilterDisabled,
        showOutsideMobile: false, // goes inside menu on mobile
      }
    : null;

  // Merge all actions
  const actions: ActionConfig[] = [
    ...(resetAction ? [resetAction] : []),
    ...(savedFilterAction ? [savedFilterAction] : []),
    ...additionalActions,
  ];

  if (isMobile) {
    // Split actions into outside vs inside
    const outsideActions = actions.filter((action) => action.showOutsideMobile);
    const menuItems = actions
      .filter((action) => !action.showOutsideMobile)
      .map((action) => ({
        onClick: action.onClick,
        dataTestId: action.testId || action.id,
        label: (
          <LabelWithIconText>
            {action.icon} {action.label}
          </LabelWithIconText>
        ),
        disabled: action.disabled,
      }));

    return (
      <SearchSaveFiltersSubHeaderWrapper>
        <MuiGrid
          size={gridSize}
          className="responsive-search-btn-container"
          spacing={0}
        >
          {outsideActions.map((outsideAction) => (
            <MuiButtonTertiary
              key={outsideAction.id}
              data-testid={outsideAction.testId || outsideAction.id}
              onClick={outsideAction.onClick}
              aria-label={outsideAction.ariaLabel}
              disabled={outsideAction.disabled}
            >
              <LabelWithIconText>
                {outsideAction.icon} {outsideAction.label}
              </LabelWithIconText>
            </MuiButtonTertiary>
          ))}
          {menuItems.length > 0 && (
            <MuiOptionMenu
              className="responsive-menu-item"
              menuItems={menuItems}
            />
          )}
        </MuiGrid>
        {additionalActions?.length > 0 ? <SeparatorDotted /> : <Separator />}
      </SearchSaveFiltersSubHeaderWrapper>
    );
  }

  return (
    <SearchSaveFiltersSubHeaderWrapper>
      <MuiGrid
        size={gridSizeSmall}
        className={`form-header-right ${
          additionalActions?.length > 0 ? "with-extra-actions" : ""
        }`}
      >
        {actions.map((action) => (
          <MuiButtonTertiary
            key={action.id}
            data-testid={action.testId || action.id}
            className={action.id === "reset-all" ? "reset-all-button" : ""}
            onClick={action.onClick}
            ref={action.id === "saved-filter" ? anchorRef : undefined}
            aria-label={action.ariaLabel}
            disabled={action.disabled}
          >
            <LabelWithIconText>
              {action.icon} {action.label}
            </LabelWithIconText>
          </MuiButtonTertiary>
        ))}
      </MuiGrid>
      {additionalActions?.length > 0 ? <SeparatorDotted /> : <Separator />}
    </SearchSaveFiltersSubHeaderWrapper>
  );
};
